import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Alert,
  Form,
  Modal,
  ListGroup
} from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { voiceAPI } from '../services/api';
import { isAuthenticated } from '../utils/auth';

function VoiceModelDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Purchase modal
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedLicenseType, setSelectedLicenseType] = useState('PERSONAL');
  const [purchasing, setPurchasing] = useState(false);

  // Synthesize modal
  const [showSynthesizeModal, setShowSynthesizeModal] = useState(false);
  const [synthesizeText, setSynthesizeText] = useState('');
  const [synthesizing, setSynthesizing] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);

  // Review modal
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    fetchModelDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchModelDetails = async () => {
    try {
      setLoading(true);
      const response = await voiceAPI.getModel(id);
      setModel(response.data);
    } catch (err) {
      console.error('Fetch model error:', err);
      setError(err.response?.data?.error || 'Failed to load model details');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseLicense = async () => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      setPurchasing(true);
      setError(null);

      // In production, integrate with Stripe for payment
      const paymentToken = 'demo_token';

      await voiceAPI.purchaseLicense(id, selectedLicenseType, paymentToken);

      setSuccess('License purchased successfully!');
      setShowPurchaseModal(false);
      fetchModelDetails(); // Refresh to show updated data
    } catch (err) {
      console.error('Purchase error:', err);
      setError(err.response?.data?.error || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  };

  const handleSynthesize = async () => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      setSynthesizing(true);
      setError(null);

      const response = await voiceAPI.synthesizeSpeech(id, synthesizeText);
      setAudioUrl(response.data.audio_url);
      setSuccess('Speech synthesized successfully!');
    } catch (err) {
      console.error('Synthesize error:', err);
      setError(err.response?.data?.error || 'Synthesis failed');
    } finally {
      setSynthesizing(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      setSubmittingReview(true);
      setError(null);

      await voiceAPI.addReview(id, reviewRating, reviewComment);

      setSuccess('Review submitted successfully!');
      setShowReviewModal(false);
      setReviewComment('');
      fetchModelDetails(); // Refresh to show new review
    } catch (err) {
      console.error('Review error:', err);
      setError(err.response?.data?.error || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <Container className="text-center" style={{ marginTop: '100px' }}>
        <Spinner animation="border" />
        <p className="mt-3">Loading model details...</p>
      </Container>
    );
  }

  if (!model) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">Model not found</Alert>
        <Button onClick={() => navigate('/marketplace')}>Back to Marketplace</Button>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Row>
        {/* Main Content */}
        <Col md={8}>
          <Card className="mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <h2>{model.name}</h2>
                  <p className="text-muted">
                    by <strong>{model.owner?.username || 'Unknown'}</strong>
                  </p>
                </div>
                <Badge bg={model.status === 'READY' ? 'success' : 'warning'} className="fs-6">
                  {model.status}
                </Badge>
              </div>

              <div className="mb-3">
                {model.language && <Badge bg="secondary" className="me-2">{model.language}</Badge>}
                {model.gender && <Badge bg="info" className="me-2">{model.gender}</Badge>}
                {model.voice_style && <Badge bg="primary" className="me-2">{model.voice_style}</Badge>}
                {model.age_range && <Badge bg="light" text="dark" className="me-2">{model.age_range}</Badge>}
              </div>

              <p className="lead">{model.description}</p>

              <div className="d-flex align-items-center mb-3">
                <div className="me-4">
                  <strong className="text-warning">
                    {model.rating?.toFixed(1) || 'N/A'} ⭐
                  </strong>
                  <span className="text-muted ms-2">
                    ({model.rating_count || 0} reviews)
                  </span>
                </div>
                <div>
                  <span className="text-muted">
                    {model.usage_count || 0} uses
                  </span>
                </div>
              </div>

              {/* Sample Audio */}
              {model.sample_audio_url && (
                <div className="mb-3">
                  <h5>Sample Audio</h5>
                  <audio controls className="w-100">
                    <source src={model.sample_audio_url} type="audio/mpeg" />
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}

              {/* Reviews */}
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5>Reviews ({model.reviews?.length || 0})</h5>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowReviewModal(true)}
                  >
                    Write a Review
                  </Button>
                </div>

                {model.reviews && model.reviews.length > 0 ? (
                  <ListGroup>
                    {model.reviews.map((review, idx) => (
                      <ListGroup.Item key={idx}>
                        <div className="d-flex justify-content-between">
                          <strong>{review.reviewer}</strong>
                          <span className="text-warning">
                            {review.rating} ⭐
                          </span>
                        </div>
                        <p className="mb-1">{review.comment}</p>
                        <small className="text-muted">
                          {new Date(review.created_at).toLocaleDateString()}
                        </small>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <p className="text-muted">No reviews yet. Be the first to review!</p>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Sidebar */}
        <Col md={4}>
          {/* Pricing Card */}
          <Card className="mb-3 sticky-top" style={{ top: '20px' }}>
            <Card.Body>
              <h4 className="text-primary mb-3">${model.base_price}</h4>

              {model.licensing_tiers && (
                <div className="mb-3">
                  <h6>License Options</h6>
                  <ListGroup className="mb-3">
                    {model.licensing_tiers.map((tier, idx) => (
                      <ListGroup.Item key={idx}>
                        <div className="d-flex justify-content-between mb-1">
                          <strong>{tier.type}</strong>
                          <span className="text-primary">${tier.price}</span>
                        </div>
                        <ul className="small mb-0">
                          {tier.features.map((feature, fidx) => (
                            <li key={fidx}>{feature}</li>
                          ))}
                        </ul>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              )}

              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => setShowPurchaseModal(true)}
                >
                  Purchase License
                </Button>

                <Button
                  variant="outline-primary"
                  onClick={() => setShowSynthesizeModal(true)}
                >
                  Try Voice Synthesis
                </Button>

                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/marketplace')}
                >
                  Back to Marketplace
                </Button>
              </div>
            </Card.Body>
          </Card>

          {/* Info Card */}
          <Card>
            <Card.Body>
              <h6>Model Information</h6>
              <ul className="list-unstyled small">
                <li><strong>Created:</strong> {new Date(model.created_at).toLocaleDateString()}</li>
                {model.nft_token_id && (
                  <li><strong>NFT Token:</strong> #{model.nft_token_id}</li>
                )}
                {model.is_tradeable && (
                  <li><Badge bg="success">Tradeable</Badge></li>
                )}
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Purchase License Modal */}
      <Modal show={showPurchaseModal} onHide={() => setShowPurchaseModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Purchase License</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>License Type</Form.Label>
              <Form.Select
                value={selectedLicenseType}
                onChange={(e) => setSelectedLicenseType(e.target.value)}
              >
                <option value="PERSONAL">Personal - ${model.base_price}</option>
                <option value="COMMERCIAL">Commercial - ${model.base_price * 5}</option>
                <option value="EXCLUSIVE">Exclusive - ${model.base_price * 50}</option>
                <option value="UNLIMITED">Unlimited - ${model.base_price * 20}</option>
              </Form.Select>
            </Form.Group>

            <Alert variant="info">
              <small>
                In production, this would integrate with Stripe for secure payment processing.
              </small>
            </Alert>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPurchaseModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handlePurchaseLicense}
            disabled={purchasing}
          >
            {purchasing ? <Spinner animation="border" size="sm" /> : 'Complete Purchase'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Synthesize Speech Modal */}
      <Modal show={showSynthesizeModal} onHide={() => setShowSynthesizeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Synthesize Speech</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Text to Synthesize</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Enter text to convert to speech..."
                value={synthesizeText}
                onChange={(e) => setSynthesizeText(e.target.value)}
              />
              <Form.Text className="text-muted">
                Requires an active license for this model.
              </Form.Text>
            </Form.Group>

            {audioUrl && (
              <div className="mb-3">
                <h6>Generated Audio</h6>
                <audio controls className="w-100">
                  <source src={audioUrl} type="audio/mpeg" />
                </audio>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSynthesizeModal(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleSynthesize}
            disabled={!synthesizeText || synthesizing}
          >
            {synthesizing ? <Spinner animation="border" size="sm" /> : 'Synthesize'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Review Modal */}
      <Modal show={showReviewModal} onHide={() => setShowReviewModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Write a Review</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Rating</Form.Label>
              <Form.Select
                value={reviewRating}
                onChange={(e) => setReviewRating(Number(e.target.value))}
              >
                <option value="5">5 ⭐ Excellent</option>
                <option value="4">4 ⭐ Very Good</option>
                <option value="3">3 ⭐ Good</option>
                <option value="2">2 ⭐ Fair</option>
                <option value="1">1 ⭐ Poor</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Comment (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Share your experience with this voice model..."
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReviewModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitReview}
            disabled={submittingReview}
          >
            {submittingReview ? <Spinner animation="border" size="sm" /> : 'Submit Review'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default VoiceModelDetail;
